import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useApi } from "../../hooks/useApi";
import { useTranslation } from "react-i18next";
import { LANGUAGES, changeLanguage } from "../../i18n";
import { API_URL } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Separator } from "../../components/ui/separator";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { toast } from "sonner";
import {
    User,
    Building2,
    Palette,
    Save,
    Moon,
    Sun,
    Receipt,
    FileText,
    ChevronRight,
    Globe,
    Check,
    Database,
    Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getInitials, cn } from "../../lib/utils";

export const SettingsPage = () => {
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { get, put } = useApi();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingLang, setSavingLang] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [seedResult, setSeedResult] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [profileData, setProfileData] = useState({
        name: "",
        phone: "",
    });
    const [tenantData, setTenantData] = useState({
        name: "",
        domain: "",
        address: "",
        phone: "",
        email: "",
        tax_id: "",
    });

    const handleLanguageChange = async (lang) => {
        setSavingLang(true);
        await changeLanguage(lang, API_URL, token);
        toast.success(t("common.success"));
        setSavingLang(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            const results = await Promise.allSettled([
                get("/settings/tenant"),
            ]);
            if (results[0].status === "fulfilled" && results[0].value) {
                const tenantRes = results[0].value;
                setTenant(tenantRes);
                setTenantData({
                    name: tenantRes.name || "",
                    domain: tenantRes.domain || "",
                    address: tenantRes.address || "",
                    phone: tenantRes.phone || "",
                    email: tenantRes.email || "",
                    tax_id: tenantRes.tax_id || "",
                });
            }
            setProfileData({
                name: user?.name || "",
                phone: user?.phone || "",
            });
            setLoading(false);
        };

        fetchData();
    }, [user, get, t]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveProfile = async () => {
        setSaving(true);
        try {
            await put("/settings/profile", profileData);
            toast.success(t("settings.profileUpdated"));
        } catch (error) {
            toast.error(t("settings.saveError"));
        } finally {
            setSaving(false);
        }
    };

    const saveTenant = async () => {
        setSaving(true);
        try {
            await put("/settings/tenant", tenantData);
            toast.success(t("settings.orgUpdated"));
        } catch (error) {
            toast.error(t("settings.saveError"));
        } finally {
            setSaving(false);
        }
    };

    const handleSeedDemo = async () => {
        setSeeding(true);
        setSeedResult(null);
        try {
            const res = await fetch(`${API_URL}/admin/seed-demo`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            const data = await res.json();
            setSeedResult(data);
            if (data.status === "seeded") {
                toast.success("Donnees de demo creees avec succes !");
            } else if (data.status === "already_seeded") {
                toast.info("Les donnees de demo existent deja.");
            } else {
                toast.error("Erreur lors du seed");
            }
        } catch {
            toast.error("Erreur lors de la creation des donnees de demo");
        } finally {
            setSeeding(false);
        }
    };

    const handleResetDemo = async () => {
        setSeeding(true);
        setSeedResult(null);
        try {
            // Step 1: Reset (delete old data)
            const resetRes = await fetch(`${API_URL}/admin/reset-demo`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            const resetData = await resetRes.json();

            // Step 2: Re-seed (create new data)
            const seedRes = await fetch(`${API_URL}/admin/seed-demo`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            const seedData = await seedRes.json();

            const allDetails = [...(resetData.details || []), "---", ...(seedData.details || [])];
            setSeedResult({ ...seedData, details: allDetails });
            toast.success("Donnees demo reinitialisees et recreees !");
        } catch {
            toast.error("Erreur lors de la reinitialisation");
        } finally {
            setSeeding(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="settings-page">
            {/* Header */}
            <div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">{t("settings.title")}</h1>
                <p className="text-muted-foreground mt-1">{t("settings.subtitle")}</p>
            </div>

            <Tabs defaultValue="profile" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="profile" data-testid="tab-profile">
                        <User className="w-4 h-4 mr-2" />
                        {t("settings.profile")}
                    </TabsTrigger>
                    <TabsTrigger value="organization" data-testid="tab-organization">
                        <Building2 className="w-4 h-4 mr-2" />
                        {t("settings.organization")}
                    </TabsTrigger>
                    <TabsTrigger value="documents" data-testid="tab-documents">
                        <FileText className="w-4 h-4 mr-2" />
                        {t("settings.documents")}
                    </TabsTrigger>
                    <TabsTrigger value="appearance" data-testid="tab-appearance">
                        <Palette className="w-4 h-4 mr-2" />
                        {t("settings.appearance")}
                    </TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">{t("settings.personalInfo")}</CardTitle>
                            <CardDescription>{t("settings.personalInfoDesc")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center gap-6">
                                <Avatar className="h-20 w-20">
                                    <AvatarImage src={user?.picture} />
                                    <AvatarFallback className="text-xl bg-primary text-primary-foreground">
                                        {getInitials(user?.name)}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <h3 className="font-medium">{user?.name}</h3>
                                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                                    <p className="text-xs text-muted-foreground mt-1">{t("settings.role")}: {t(`roles.${user?.role}`)}</p>
                                </div>
                            </div>

                            <Separator />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="profile-name">{t("settings.fullName")}</Label>
                                    <Input
                                        id="profile-name"
                                        value={profileData.name}
                                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                        data-testid="profile-name-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="profile-phone">{t("common.phone")}</Label>
                                    <Input
                                        id="profile-phone"
                                        value={profileData.phone}
                                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                        data-testid="profile-phone-input"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? t("settings.saving") : t("common.save")}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Organization Tab */}
                <TabsContent value="organization" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">{t("settings.orgInfo")}</CardTitle>
                            <CardDescription>{t("settings.orgInfoDesc")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="org-name">{t("settings.companyName")}</Label>
                                    <Input
                                        id="org-name"
                                        value={tenantData.name}
                                        onChange={(e) => setTenantData({ ...tenantData, name: e.target.value })}
                                        data-testid="org-name-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="org-domain">{t("settings.domain")}</Label>
                                    <Input
                                        id="org-domain"
                                        value={tenantData.domain}
                                        onChange={(e) => setTenantData({ ...tenantData, domain: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="org-email">{t("settings.contactEmail")}</Label>
                                    <Input
                                        id="org-email"
                                        type="email"
                                        value={tenantData.email}
                                        onChange={(e) => setTenantData({ ...tenantData, email: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="org-phone">{t("common.phone")}</Label>
                                    <Input
                                        id="org-phone"
                                        value={tenantData.phone}
                                        onChange={(e) => setTenantData({ ...tenantData, phone: e.target.value })}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label htmlFor="org-address">{t("settings.address")}</Label>
                                    <Input
                                        id="org-address"
                                        value={tenantData.address}
                                        onChange={(e) => setTenantData({ ...tenantData, address: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="org-tax">{t("settings.taxId")}</Label>
                                    <Input
                                        id="org-tax"
                                        value={tenantData.tax_id}
                                        onChange={(e) => setTenantData({ ...tenantData, tax_id: e.target.value })}
                                    />
                                </div>
                            </div>

                            <Separator />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                                    <div>
                                        <p className="font-medium">{t("settings.currentPlan")}</p>
                                        <p className="text-sm text-muted-foreground capitalize">{tenant?.plan || "Basic"}</p>
                                    </div>
                                    <Button variant="outline" onClick={() => navigate("/settings/subscription")} data-testid="manage-subscription-btn">
                                        {t("settings.upgrade")}
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                                    <div>
                                        <p className="font-medium">Devise</p>
                                        <p className="text-sm text-muted-foreground font-mono">{tenant?.currency || "EUR"}</p>
                                    </div>
                                    <Badge variant="outline" className="text-xs" data-testid="currency-badge">
                                        Definie par l'admin
                                    </Badge>
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <Button onClick={saveTenant} disabled={saving} data-testid="save-org-btn">
                                    <Save className="w-4 h-4 mr-2" />
                                    {saving ? t("settings.saving") : t("common.save")}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Seed Demo Data */}
                    <Card className="border-dashed border-2 border-muted-foreground/20">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Database className="w-5 h-5" />
                                Donnees de demonstration
                            </CardTitle>
                            <CardDescription>
                                Generez un jeu de donnees complet pour tester l'application : 2 utilisateurs demo, 8 clients, 8 projets avec taches, devis, factures et conversations de groupe.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-3 flex-wrap">
                                <Button
                                    onClick={handleSeedDemo}
                                    disabled={seeding}
                                    variant="outline"
                                    data-testid="seed-demo-btn"
                                >
                                    {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                                    {seeding ? "En cours..." : "Generer les donnees"}
                                </Button>
                                <Button
                                    onClick={handleResetDemo}
                                    disabled={seeding}
                                    variant="destructive"
                                    size="sm"
                                    data-testid="reset-demo-btn"
                                >
                                    {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                    Reinitialiser et regenerer
                                </Button>
                                {seedResult && (
                                    <Badge variant={seedResult.status === "seeded" ? "default" : "secondary"} data-testid="seed-result-badge">
                                        {seedResult.status === "seeded" ? "Donnees creees" : seedResult.status === "already_seeded" ? "Deja present" : seedResult.status === "reset" ? "Reinitialise" : "Erreur"}
                                    </Badge>
                                )}
                            </div>
                            {seedResult?.details && (
                                <div className="mt-4 p-3 bg-muted rounded-lg text-sm space-y-1" data-testid="seed-details">
                                    {seedResult.details.map((line, i) => (
                                        <p key={i} className="flex items-center gap-2">
                                            <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            )}
                            {seedResult?.demo_accounts && (
                                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800" data-testid="seed-credentials">
                                    <p className="font-medium text-sm mb-2">Comptes de demonstration :</p>
                                    {seedResult.demo_accounts.map((acc, i) => (
                                        <div key={i} className="text-sm text-muted-foreground mb-1">
                                            <span className="font-medium text-foreground">{acc.role}</span> : {acc.email} / <code className="bg-muted px-1 rounded">{acc.password}</code>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" className="space-y-4">
                    <Card
                        className="cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => navigate("/settings/billing")}
                        data-testid="billing-settings-link"
                    >
                        <CardContent className="flex items-center justify-between p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Receipt className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-medium">{t("settings.billingSettings")}</h3>
                                    <p className="text-sm text-muted-foreground">{t("settings.billingSettingsDesc")}</p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </CardContent>
                    </Card>
                    <Card
                        className="cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => navigate("/settings/quotes")}
                        data-testid="quote-settings-link"
                    >
                        <CardContent className="flex items-center justify-between p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-medium">{t("settings.quoteSettings")}</h3>
                                    <p className="text-sm text-muted-foreground">{t("settings.quoteSettingsDesc")}</p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Appearance Tab */}
                <TabsContent value="appearance" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading">{t("settings.appearance")}</CardTitle>
                            <CardDescription>{t("settings.appearanceDesc")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    {theme === "dark" ? (
                                        <Moon className="w-5 h-5 text-muted-foreground" />
                                    ) : (
                                        <Sun className="w-5 h-5 text-muted-foreground" />
                                    )}
                                    <div>
                                        <p className="font-medium">{t("settings.darkMode")}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {theme === "dark" ? t("settings.enabled") : t("settings.disabled")}
                                        </p>
                                    </div>
                                </div>
                                <Switch
                                    checked={theme === "dark"}
                                    onCheckedChange={toggleTheme}
                                    data-testid="theme-switch"
                                />
                            </div>

                            <Separator />

                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => theme !== "light" && toggleTheme()}
                                    className={`p-4 rounded-lg border-2 transition-all ${
                                        theme === "light" ? "border-primary" : "border-border hover:border-primary/50"
                                    }`}
                                >
                                    <div className="w-full h-24 bg-white rounded-md border mb-2 flex items-center justify-center">
                                        <Sun className="w-8 h-8 text-yellow-500" />
                                    </div>
                                    <p className="text-sm font-medium">{t("settings.lightMode")}</p>
                                </button>
                                <button
                                    onClick={() => theme !== "dark" && toggleTheme()}
                                    className={`p-4 rounded-lg border-2 transition-all ${
                                        theme === "dark" ? "border-primary" : "border-border hover:border-primary/50"
                                    }`}
                                >
                                    <div className="w-full h-24 bg-zinc-900 rounded-md border border-zinc-800 mb-2 flex items-center justify-center">
                                        <Moon className="w-8 h-8 text-blue-400" />
                                    </div>
                                    <p className="text-sm font-medium">{t("settings.darkMode")}</p>
                                </button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Language Selection */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Globe className="w-5 h-5" />
                                {t("settings.language")}
                            </CardTitle>
                            <CardDescription>{t("settings.languageDescription")}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-3 gap-3">
                                {LANGUAGES.map((lang) => (
                                    <button
                                        key={lang.code}
                                        onClick={() => handleLanguageChange(lang.code)}
                                        disabled={savingLang}
                                        data-testid={`lang-${lang.code}`}
                                        className={cn(
                                            "flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all",
                                            i18n.language === lang.code
                                                ? "border-primary bg-primary/5"
                                                : "border-border hover:border-primary/50",
                                            savingLang && "opacity-50 cursor-not-allowed"
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-lg">{lang.flag}</span>
                                            <span className="text-sm font-medium">{lang.label}</span>
                                        </div>
                                        {i18n.language === lang.code && (
                                            <Check className="w-4 h-4 text-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};
